/*
Author: Jude Fernando
Date: 15/10/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import {DropDown} from "@/components/Atoms";
import InputField from "@/components/Atoms/InputField";
import InputLabel from "@/components/Atoms/InputLabel";
import {ValidationManager} from "@/components/survey/SurveyModel";
import {DropDownOption,INode,ItypeName} from "@/components/survey/SurveyModel/types/nodeTypes";
import {RootState} from "@/reducers/store";
import {useAddNewField,useUpdateForm} from "@/state/survey/formSlice";
import {Validate} from "@/utils";
import React,{useEffect,useRef,useState} from "react";
import {useSelector} from "react-redux";
import {FormValues} from "../../SurveyModel/types/commonTypes";

interface Iheader {
   type: string
   name: string
}
interface rowData {
    name: string
    type:string
}

interface IrowItem {
    key:string;
    items:rowData[] 
}


interface SmokingAnswer {
    type: string;   // e.g., "cigarate", "ciga", "Cigars", etc.
    perDay: number; // Number of items consumed per day
    years: number;  // Number of years of consumption
}
interface IhelthImprovmentAnswer {
    smoking: string; // Assuming it's either 'yes' or 'no'
    smokingAnswers: SmokingAnswer[];
}


const HealthImprovementPOA = (props: any) => {
    const { node, } = props;
    const { fieldHeader = [], answerQuestions=[], addLabel = "Add", removeLabel = "Remove", allowMultiple = false, options } = node as INode;
    const addNewFieldValue = useAddNewField();
    const updateFormData = useUpdateForm();
    const form = useSelector((state:RootState) => state.form);
    const [isFocused, setIsFocused] = useState(false);
    const [nodeLoaded, setNodeLoaded] = useState(false);
    const [optionLoaded, setOptionLoaded] = useState(false);
    const [typeOptions, setTypeOptions] = useState<DropDownOption[]>([]);
    const [excludeOptions, setExcludeOptions] = useState<DropDownOption[]>([]);
    const [ rows, setRows] = useState<IrowItem[]>([]);
    const divRef = useRef<HTMLDivElement | null>(null);

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        // Check if the focus is leaving the entire div
        if (!divRef.current?.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
          
        const answer: IhelthImprovmentAnswer = {
            smoking: "yes",
            smokingAnswers:[]

        }
        if(rows.length){
            const validRows = rows.filter(row => row.key !== 'placeHolder');
            const smokingAnswersList : SmokingAnswer[]= []
           
            validRows.map((row: IrowItem)=>{
                const answerField: SmokingAnswer = {type: '', perDay: 0, years: 0}
                const key = row.key;
                const namePrefix = `${node.type + key}`;
                row.items.map((rowItem: rowData)=>{
                   const fieldKey: string = rowItem.name.replace(namePrefix, '');
                   if(fieldKey in answerField){
                    const value: FormValues = form[rowItem.name].value;
                    if (value !== undefined) {
                        if (fieldKey === 'type' && typeof value === 'string') {
                            answerField[fieldKey as 'type'] = value; // for `type`, assign string value
                        } else if (fieldKey === 'perDay' || fieldKey === 'years') {
                            answerField[fieldKey as 'perDay' | 'years'] = Number(value); // for `perDay` and `years`, convert to number
                        }
                    }
                   }
                 
                })
                smokingAnswersList.push(answerField);
            })
            answer.smokingAnswers = smokingAnswersList;
        }
        }
      };
    
      const handleFocus = () => {
        if (!isFocused) {
          setIsFocused(true);
        }
      };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        if(name === 'placeHolderType'){

            const updatedRows = rows.filter(row => row.key !== 'placeHolder');
            const rowItem:IrowItem = {
                key: value,
                items:[]
            }
            fieldHeader.map((field:ItypeName, index:number)=>{
                const fieldName = `${node.type + value}`;
                const fieldNode = {
                    id:index,
                    validation: field.validation,
                    name: fieldName,
                    type: field.type,
                    value: field.type === 'singleSelect' ?  value : ''
                }
                rowItem.items.push({name: fieldName, type: field.type})
                const validatedForm = ValidationManager.applyCommonValidationsWithValue(fieldNode, '')
                addNewFieldValue(fieldName, validatedForm);
            })
            setRows([...updatedRows, rowItem ]);
            prepareExcludeTypeOptions(node.options, [...updatedRows, rowItem ])
        }
        const validatedForm = Validate.input(name, value, form, true);
        updateFormData(validatedForm);
      
    }

    useEffect(()=>{
        if(node.type && !nodeLoaded) {
            setNodeLoaded(true);
            const rowItemMap: IrowItem[] = []


            answerQuestions.map((option)=>{
                const keys = Object.keys(option);
                const rowItem:IrowItem = {
                    key: keys[0],
                    items:[]
                }
                Object.values(option).map((subNode)=>{
                    subNode.map((element)=>{
                       if(element.name){
                        rowItem.items.push({name: element.name, type: element.type})
                        const validatedForm = ValidationManager.applyCommonValidationsWithValue(element, '')
                        addNewFieldValue(element.name, validatedForm);
                       } 
                    })
                })
                rowItemMap.push(rowItem);
            });

            setRows([...rows, ...rowItemMap ])
        }
      
    },[node, nodeLoaded]);


    useEffect(()=>{
        if(options?.length && rows.length && !optionLoaded){
            prepareTypeOptions(options as string[]);
            prepareExcludeTypeOptions(options as string[], rows)
            setOptionLoaded(true);
        }
        
    }, [options, rows])

    const addNewRow = ()=> {
       // Check if 'placeHolder' already exists
        const isPlaceHolderExists = rows.some(row => row.key === 'placeHolder');
       
        if (!isPlaceHolderExists) {
            const rowItem:IrowItem = {
                key: 'placeHolder',
                items:[
                    {name: 'placeHolderType', type: 'singleSelect'},
                    {name: 'placeHolderPerDay', type: 'number'},
                    {name: 'placeHolderPerYear', type: 'number'},
                ]
            }
            setRows([...rows, rowItem]);
        }
    }
    const filterItemBasedOnNamme = (data: rowData, rowItems: IrowItem[]): IrowItem[]=> {
        return rowItems.filter(rowItem =>
            !rowItem.items.some(item => item.name === data.name)
        );
      };

    const prepareTypeOptions = (options:string[])=>{
        const optionList : DropDownOption[]= []
        options.map((data)=>{
                const optionItem = {
                    value : data,
                    label:data
                }
                optionList.push(optionItem);
        });
        setTypeOptions(optionList);
    }

    const prepareExcludeTypeOptions = (options:string[], rowItems:IrowItem[])=>{
        const optionList : DropDownOption[]= []
        options.map((data)=>{
           const itemFound: boolean =  rowItems.some((rowItem)=>{
               return  rowItem.key === data
            });
           if(itemFound){ 
                const optionItem = {
                    value : data,
                    label:data
                }
                optionList.push(optionItem);
           }
        });
        setExcludeOptions(optionList);
    }



    const removeAnswer = (element: rowData) => {
       const filteredRows = filterItemBasedOnNamme(element, rows)
        setRows(filteredRows)
      }
      
    const renderComponents = (rowElement:IrowItem, index: number) => {
        const createField = ()=> {
            return  rowElement.items.map((item: rowData, rowIndex: number)=>{
                return (
                    <React.Fragment key={index}>
                        
                            {item.type === 'singleSelect' ? <DropDown excludeOptions={excludeOptions} options={typeOptions} handleChange={handleChange}  value={rowElement.key === 'placeHolder' ? "" : form[item.name].value} name={item.name} /> :  <InputField type={item.type} value={rowElement.key === 'placeHolder' ? '' : form[item.name].value} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>handleChange(e)} name={item.name}/>}     
                           
                        {rowElement.items.length -1 === rowIndex ? allowMultiple && <div className="text-center mt-4"><button className="text-blue-700 underline" type="button" onClick={ () => removeAnswer(item)}>{`${removeLabel}`}</button></div> : null }
                    </React.Fragment>
                );
            })
        }

        return (<React.Fragment>
        {createField()}
        </React.Fragment>)
       
         
  
      };

    return (
        <div className={`matrix ${node.classes}`} ref={divRef} onBlur={handleBlur} onFocus={handleFocus}>
          <h2 className="text-2xl mb-4">{node.title}</h2>
          <div className={`py-2 grid grid-cols-${fieldHeader.length} gap-3`}>
                {
                  fieldHeader.map( (h:Iheader, i:number)=>{
                    return (
                      <InputLabel  key={i}variant="secondary">{h.name}</InputLabel>
                      )
                  }) 
                }
        </div>
                {rows.map( (rowItem, i) =>{
                     return <React.Fragment key={i}>
                        <div key={i} className={`py-2 grid grid-cols-${fieldHeader.length} gap-4`}> 
                            {renderComponents(rowItem, i)}
                        </div>
              </React.Fragment>
            })
          }
            { allowMultiple && <div className="text-center mt-8"><button className="text-blue-700 underline" type="button" onClick={ () => addNewRow()}>{addLabel}</button></div>}
        </div>
    )
}

export default HealthImprovementPOA;
